import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { useAnchoredPosition } from "hooks/useAnchoredPosition";
import type {
  AnchoredPopoverPlacement,
  AnchoredPopoverPosition,
  AnchoredPopoverProps,
} from "./types";

const DEFAULT_ESTIMATED_HEIGHT = 240;
const DEFAULT_OFFSET = 4;

export const AnchoredPopover = ({
  isOpen,
  anchorRef,
  onClose,
  children,
  align = "left",
  preferredPlacement = "bottom",
  estimatedHeight = DEFAULT_ESTIMATED_HEIGHT,
  offset = DEFAULT_OFFSET,
  className,
}: AnchoredPopoverProps) => {
  const popoverRef = useRef<HTMLDivElement>(null);

  const position = useAnchoredPosition<AnchoredPopoverPosition>({
    isOpen,
    anchorRef,
    compute: (anchorRect) => {
      const spaceBelow = window.innerHeight - anchorRect.bottom;
      const spaceAbove = anchorRect.top;

      const fitsPreferred =
        preferredPlacement === "bottom"
          ? spaceBelow >= estimatedHeight
          : spaceAbove >= estimatedHeight;
      const oppositeHasMoreRoom =
        preferredPlacement === "bottom"
          ? spaceAbove > spaceBelow
          : spaceBelow > spaceAbove;
      const oppositePlacement: AnchoredPopoverPlacement =
        preferredPlacement === "bottom" ? "top" : "bottom";
      const placement =
        !fitsPreferred && oppositeHasMoreRoom
          ? oppositePlacement
          : preferredPlacement;

      const top =
        placement === "bottom"
          ? anchorRect.bottom + window.scrollY + offset
          : anchorRect.top + window.scrollY - offset;
      const left =
        align === "left"
          ? anchorRect.left + window.scrollX
          : anchorRect.right + window.scrollX;

      return { top, left, placement, align };
    },
  });

  useEffect(() => {
    if (!isOpen) return;

    const handleOutsidePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedAnchor = anchorRef.current?.contains(target);
      const clickedPopover = popoverRef.current?.contains(target);
      if (!clickedAnchor && !clickedPopover) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleOutsidePointerDown);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsidePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose, anchorRef]);

  if (!isOpen || !position) return null;

  const translateX = position.align === "right" ? "-100%" : "0";
  const translateY = position.placement === "top" ? "-100%" : "0";
  const transform = `translate(${translateX}, ${translateY})`;

  return createPortal(
    <div
      ref={popoverRef}
      className={clsx("absolute z-50", className)}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        transform,
      }}
    >
      {children}
    </div>,
    document.body,
  );
};
